import { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { Plus, Trash2, X, GripVertical, Save, Loader2, FileText } from 'lucide-react';
import { api } from '../../lib/auth';
import { toast } from 'sonner';
import { StartStopTemplateOptions } from './StartStopTemplateOptions';
import { useI18nStore } from '../../lib/i18n';

/**
 * Depo Form Şablonu Editörü
 * --------------------------------------------------------------
 * Şub 03, 2026 — Faz 1.
 * Bir planning_depot için çalışanın günlük form alanlarını tanımlar.
 *
 * Desteklenen tipler: text, number, time, date, photo, boolean, select
 * (Kullanıcı isteği üzerine yeni tipler eklenebilir.)
 *
 * Backend: GET/PUT /api/planning/depots/{depot_id}/form-template
 */
const FIELD_TYPES = [
  { value: 'text', label: 'Metin' },
  { value: 'number', label: 'Sayı' },
  { value: 'time', label: 'Saat' },
  { value: 'date', label: 'Tarih' },
  { value: 'photo', label: 'Foto Yükleme' },
  { value: 'boolean', label: 'Evet/Hayır' },
  { value: 'select', label: 'Seçim Listesi' },
];

const blankField = () => ({
  key: '',
  label: '',
  type: 'text',
  required: false,
  options: [],
  help: '',
  default_now: false, // Saat/Tarih alanları için: şu anki değerle otomatik doldur (sadece time/date)
  validation: {},     // Cross-field/numeric kuralları (ör. {integer:true, greater_than:'start_km'})
});

// ─── Varsayılan dosya yükleme formu (Haz 14, 2026) ────────────────────────
// Operatör isteği: her rotada default olarak şu 4 alan bulunsun → tek tıkla
// ekle, gerekirse düzenle. Cross-field kuralları `validation` ile metadata
// olarak tutuluyor; form çalışan tarafından doldurulurken consumer
// (`DepotPlanningForm`) bu kurallara göre Bitiş > Başlangıç kontrolü yapar.
const DEFAULT_FIELDS = [
  {
    key: 'start_km',
    label: 'Başlangıç KM',
    type: 'number',
    required: true,
    options: [],
    help: 'Tur başlangıcındaki kilometre değeri',
    default_now: false,
    validation: { integer: true, min: 0 },
  },
  {
    key: 'end_km',
    label: 'Bitiş KM',
    type: 'number',
    required: true,
    options: [],
    help: 'Başlangıç KM\'den büyük olmalı',
    default_now: false,
    validation: { integer: true, greater_than: 'start_km' },
  },
  {
    key: 'start_time',
    label: 'Başlangıç Saat',
    type: 'time',
    required: true,
    options: [],
    help: '',
    default_now: false,
    validation: {},
  },
  {
    key: 'end_time',
    label: 'Bitiş Saat',
    type: 'time',
    required: true,
    options: [],
    help: 'Başlangıç saatinden sonra olmalı; modal açıldığında şimdiki saat otomatik doldurulur',
    default_now: true,
    validation: { greater_than: 'start_time' },
  },
];

const slugifyKey = (label) =>
  (label || '')
    .toLowerCase()
    .replace(/[ığüşöç]/g, (m) => ({ ı: 'i', ğ: 'g', ü: 'u', ş: 's', ö: 'o', ç: 'c' }[m] || m))
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 48);

export function DepotFormTemplateDialog({ open, depot, company, onClose, onSaved, withDefaults = false }) {
  const isCompany = !!company;
  const target = isCompany ? company : depot;
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Faz 6: hangi rotalara uygulansın? (sadece depot mode)
  const [allRoutes, setAllRoutes] = useState([]);
  const [selectedRoutes, setSelectedRoutes] = useState([]);
  // Şub 16, 2026 — Çalışana planlama bildirimi/maili gönderilsin mi? Default OFF.
  const [notifyEmployee, setNotifyEmployee] = useState(false);
  const [startStop, setStartStop] = useState({ start_stop_enabled: false, start_stop_required: false, start_stop_hide_form: false, start_stop_hide_route_report: false, start_stop_fields: {}, start_stop_window_start: '', start_stop_window_end: '', start_stop_preset: {} });
  const [depotLocation, setDepotLocation] = useState(null);

  useEffect(() => {
    if (!open || !target?.id) return;
    let cancelled = false;
    setLoading(true);
    const promises = isCompany
      ? [api.get(`/planning/companies/${target.id}/form-template`)]
      : [
          api.get(`/planning/depots/${target.id}/form-template`),
          api.get(`/planning/routes`, { params: { depot_id: target.id } }),
        ];
    Promise.all(promises)
      .then((results) => {
        if (cancelled) return;
        const data = results[0].data?.data;
        let nextFields = (data?.fields || []).map((f) => ({
          ...f,
          options: f.options || [],
          help: f.help || '',
          default_now: !!f.default_now,
          validation: f.validation || {},
        }));
        // withDefaults=true: dialog "Varsayılan" butonundan açıldıysa, mevcut
        // alanlara dokunmadan eksik default'ları başa ekle. (Tam ön-doldurma
        // — kullanıcı kaydet'e basana kadar DB değişmez, istemezse iptal eder.)
        if (withDefaults) {
          const existingKeys = new Set(nextFields.map((f) => f.key));
          const toPrepend = DEFAULT_FIELDS
            .filter((d) => !existingKeys.has(d.key))
            .map((d) => ({ ...d, options: [...(d.options || [])] }));
          if (toPrepend.length) nextFields = [...toPrepend, ...nextFields];
        }
        setFields(nextFields);
        // Bildirim toggle'ı (depot veya company şablonundan oku)
        setNotifyEmployee(!!data?.notify_employee);
        setStartStop({ start_stop_enabled: !!data?.start_stop_enabled, start_stop_required: !!data?.start_stop_required, start_stop_hide_form: !!data?.start_stop_hide_form, start_stop_hide_route_report: !!data?.start_stop_hide_route_report, start_stop_fields: data?.start_stop_fields || {}, start_stop_window_start: data?.start_stop_window_start || '', start_stop_window_end: data?.start_stop_window_end || '', start_stop_preset: data?.start_stop_preset || {} });
        setDepotLocation(data?.depot_location || null);
        if (!isCompany) {
          setSelectedRoutes(data?.route_ids || []);
          setAllRoutes(results[1].data?.data || []);
        }
      })
      .catch(() => toast.error('Şablon yüklenemedi'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [open, target, isCompany]);

  if (!open) return null;

  const updateField = (idx, patch) => {
    setFields((prev) => prev.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  };
  const removeField = (idx) => {
    setFields((prev) => prev.filter((_, i) => i !== idx));
  };
  const addField = () => {
    setFields((prev) => [...prev, blankField()]);
  };
  const addPhaseField = (phase, label, type) => {
    setFields((prev) => {
      let key = slugifyKey(label) || 'alan';
      while (prev.some((f) => f.key === key)) key = `${key}_2`;
      return [...prev, { ...blankField(), label, key, type, ss_phase: phase }];
    });
  };
  // Eksik default alanları ekler (zaten varolanlara dokunmaz). Aynı işlemin
  // dialog body içindeki butonla erişimi. İdempotent: hepsi varsa sessiz info.
  const insertDefaults = () => {
    setFields((prev) => {
      const existingKeys = new Set(prev.map((f) => f.key));
      const toPrepend = DEFAULT_FIELDS
        .filter((d) => !existingKeys.has(d.key))
        .map((d) => ({ ...d, options: [...(d.options || [])] }));
      if (toPrepend.length === 0) {
        toast.info('Varsayılan alanlar zaten ekli');
        return prev;
      }
      toast.success(`${toPrepend.length} varsayılan alan eklendi`);
      return [...toPrepend, ...prev];
    });
  };
  const moveField = (idx, delta) => {
    setFields((prev) => {
      const ni = idx + delta;
      if (ni < 0 || ni >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[ni]] = [next[ni], next[idx]];
      return next;
    });
  };

  const save = async () => {
    // Local validation
    const cleaned = fields.map((f) => ({
      ...f,
      key: f.key?.trim() || slugifyKey(f.label),
      label: f.label?.trim(),
    }));
    for (const f of cleaned) {
      if (!f.label) { toast.error('Tüm alanlar için bir etiket girin'); return; }
      if (!f.key) { toast.error(`"${f.label}" için anahtar (key) boş — etiket doldurulduğunda otomatik üretilir`); return; }
      if (f.type === 'select' && (!f.options || f.options.length === 0)) {
        toast.error(`"${f.label}" seçim listesi için en az 1 seçenek girin`); return;
      }
    }
    const keys = cleaned.map((f) => f.key);
    if (new Set(keys).size !== keys.length) {
      toast.error('Anahtarlar (key) benzersiz olmalı'); return;
    }
    setSaving(true);
    try {
      const url = isCompany
        ? `/planning/companies/${target.id}/form-template`
        : `/planning/depots/${target.id}/form-template`;
      const ssBody = { start_stop_enabled: !!startStop.start_stop_enabled, start_stop_required: !!startStop.start_stop_required, start_stop_hide_form: !!startStop.start_stop_hide_form, start_stop_hide_route_report: !!startStop.start_stop_hide_route_report, start_stop_fields: startStop.start_stop_fields || {}, start_stop_window_start: startStop.start_stop_window_start || null, start_stop_window_end: startStop.start_stop_window_end || null, start_stop_preset: startStop.start_stop_preset || {} };
      const body = isCompany
        ? { fields: cleaned, route_ids: [], notify_employee: notifyEmployee, ...ssBody }
        : { fields: cleaned, route_ids: selectedRoutes, notify_employee: notifyEmployee, ...ssBody };
      await api.put(url, body);
      toast.success('Form şablonu kaydedildi');
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Kaydedilemedi');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-slate-900 rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="depot-form-template-dialog"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-500" />
            <div>
              <h2 className="text-base font-bold text-slate-800 dark:text-slate-100">
                {isCompany ? 'Şirket Geneli Form Şablonu' : 'Form Şablonu'}
              </h2>
              <p className="text-xs text-slate-500">
                {target?.name}
                {isCompany && <span className="ml-1 text-blue-600">(tüm depolar için fallback)</span>}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded" data-testid="depot-form-template-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading ? (
            <div className="text-center py-8 text-sm text-slate-400 flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Yükleniyor…
            </div>
          ) : (
            <>
              <StartStopTemplateOptions value={startStop} onChange={setStartStop} fields={fields} setFields={setFields} onAddField={addPhaseField}
                isCompany={isCompany} depotLocation={depotLocation} routes={isCompany ? [] : (selectedRoutes.length ? allRoutes.filter((r) => selectedRoutes.includes(r.id)) : allRoutes)} />
              {/* Şub 16, 2026 — Çalışan bildirim/mail toggle.
                  Default OFF; sunum/test planlamaları sırasında spam atmamak için.
                  Yalnızca burada açıkça enable edilirse atama yapılınca bildirim gider. */}
              <div className={`border rounded-lg p-3 space-y-1 ${notifyEmployee ? 'border-emerald-300 dark:border-emerald-700 bg-emerald-50/40 dark:bg-emerald-950/20' : 'border-amber-200 dark:border-amber-800 bg-amber-50/40 dark:bg-amber-950/20'}`}>
                <label className="flex items-center gap-3 cursor-pointer" data-testid="form-template-notify-toggle-label">
                  <input
                    type="checkbox"
                    checked={notifyEmployee}
                    onChange={(e) => setNotifyEmployee(e.target.checked)}
                    className={`w-4 h-4 ${notifyEmployee ? 'accent-emerald-600' : 'accent-amber-500'}`}
                    data-testid="form-template-notify-toggle"
                  />
                  <div className="flex-1">
                    <div className={`text-xs font-semibold flex items-center gap-2 ${notifyEmployee ? 'text-emerald-700 dark:text-emerald-300' : 'text-amber-700 dark:text-amber-300'}`}>
                      {notifyEmployee ? '🔔 Çalışana bildirim/mail gönderilsin' : '🔕 Bildirim/mail KAPALI'}
                    </div>
                    <div className="text-[11px] text-slate-600 dark:text-slate-400 mt-0.5">
                      {notifyEmployee
                        ? 'Atama yapıldığında çalışan push + uygulama-içi bildirim + e-posta alır.'
                        : 'Atama yapıldığında çalışana hiçbir şey iletilmez (test/deneme planlamaları için ideal).'}
                    </div>
                  </div>
                </label>
              </div>

              {/* Faz 6: Route uygulanma kapsamı (sadece depot mode) */}
              {!isCompany && (
              <div className="border border-blue-200 dark:border-blue-800 rounded-lg p-3 bg-blue-50/40 dark:bg-blue-950/20 space-y-2">
                <div className="text-xs font-semibold text-blue-700 dark:text-blue-300 flex items-center justify-between gap-2">
                  <span>📍 Hangi rotalar form alsın?</span>
                  <span className="text-[10px] text-blue-500 font-normal">
                    {selectedRoutes.length === 0
                      ? `Tümü (${allRoutes.length})`
                      : `${selectedRoutes.length}/${allRoutes.length} aktif`}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-xs flex-wrap">
                  <button
                    type="button"
                    onClick={() => setSelectedRoutes([])}
                    className={`px-2 py-1 rounded text-[11px] font-medium transition ${
                      selectedRoutes.length === 0
                        ? 'bg-blue-600 text-white'
                        : 'bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300'
                    }`}
                    data-testid="form-template-scope-all"
                  >Tümünü Aç</button>
                  <button
                    type="button"
                    onClick={() => setSelectedRoutes(allRoutes.map((r) => r.id))}
                    className="px-2 py-1 rounded text-[11px] font-medium bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300"
                    data-testid="form-template-select-all"
                  >Hepsini Seç</button>
                  <button
                    type="button"
                    onClick={() => setSelectedRoutes([])}
                    className="px-2 py-1 rounded text-[11px] font-medium bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300"
                    data-testid="form-template-clear-all"
                  >Hiçbiri</button>
                </div>
                {/* Toggle-style route listesi: her rota için ayrı aç/kapa.
                    "Tümü" modunda (boş array) tüm satırlar açık görünür ve
                    kapatılınca otomatik selected-mode'a geçer. */}
                <div className="max-h-48 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-900 divide-y divide-slate-100 dark:divide-slate-800">
                  {allRoutes.length === 0 && (
                    <div className="text-[11px] text-slate-400 italic px-2 py-2">Bu depoya bağlı rota yok.</div>
                  )}
                  {allRoutes.map((r) => {
                    const isAllMode = selectedRoutes.length === 0;
                    const isOn = isAllMode || selectedRoutes.includes(r.id);
                    return (
                      <div key={r.id} className="flex items-center justify-between gap-2 px-2 py-1.5 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-mono font-semibold text-xs">{r.route_code}</span>
                          {r.route_name && <span className="text-[11px] text-slate-500 truncate">— {r.route_name}</span>}
                        </div>
                        {/* Tailwind toggle: yeşil=form al, gri=form alma */}
                        <button
                          type="button"
                          onClick={() => {
                            if (isAllMode) {
                              // Tümü açıkken bir tanesi kapatılırsa: kalan tüm rotaları seçili kabul et,
                              // bu rota'yı seçili olmaktan çıkar.
                              setSelectedRoutes(allRoutes.filter((x) => x.id !== r.id).map((x) => x.id));
                            } else if (isOn) {
                              const next = selectedRoutes.filter((x) => x !== r.id);
                              // Hiçbiri kalmadıysa "tümü" yerine "hiçbiri" anlamı için boş bırakmıyoruz —
                              // burada SADECE bu rotayı çıkarıyoruz; sonraki state boş kalabilir.
                              setSelectedRoutes(next);
                            } else {
                              setSelectedRoutes([...selectedRoutes, r.id]);
                            }
                          }}
                          className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors ${
                            isOn ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-700'
                          }`}
                          data-testid={`form-template-route-toggle-${r.id}`}
                          title={isOn ? 'Form alıyor — kapatmak için tıkla' : 'Form almıyor — açmak için tıkla'}
                        >
                          <span
                            className={`inline-block h-4 w-4 mt-0.5 rounded-full bg-white shadow transform transition-transform ${
                              isOn ? 'translate-x-4' : 'translate-x-0.5'
                            }`}
                          />
                        </button>
                      </div>
                    );
                  })}
                </div>
                <p className="text-[10px] text-slate-500 italic">
                  💡 Yeşil = form alır, gri = form almaz. &quot;Tümünü Aç&quot; tek tıkla her rotayı dahil eder.
                </p>
              </div>
              )}

              {fields.length === 0 ? (
                <div className="text-center py-12 text-sm text-slate-400">
                  Bu depo için henüz form alanı tanımlanmamış.<br />
                  <span className="text-xs">Aşağıdaki butonla başlayın.</span>
                </div>
              ) : (
                fields.map((f, idx) => (
              <div key={idx} className="border border-slate-200 dark:border-slate-700 rounded-lg p-3 bg-slate-50 dark:bg-slate-800" data-testid={`depot-form-field-${idx}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex flex-col gap-0.5">
                    <button onClick={() => moveField(idx, -1)} className="text-slate-400 hover:text-slate-700 text-xs disabled:opacity-30" disabled={idx === 0} title="Yukarı">▲</button>
                    <button onClick={() => moveField(idx, +1)} className="text-slate-400 hover:text-slate-700 text-xs disabled:opacity-30" disabled={idx === fields.length - 1} title="Aşağı">▼</button>
                  </div>
                  <GripVertical className="h-4 w-4 text-slate-300" />
                  <Input
                    value={f.label}
                    onChange={(e) => updateField(idx, { label: e.target.value, key: f.key || slugifyKey(e.target.value) })}
                    placeholder="Etiket (örn. Başlangıç KM)"
                    className="h-8 text-sm flex-1"
                    data-testid={`depot-form-field-label-${idx}`}
                  />
                  <select
                    value={f.type}
                    onChange={(e) => updateField(idx, { type: e.target.value, options: e.target.value === 'select' ? (f.options || []) : [] })}
                    className="h-8 text-xs rounded border bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 px-2 w-32"
                    data-testid={`depot-form-field-type-${idx}`}
                  >
                    {FIELD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  <label className="flex items-center gap-1 text-xs text-slate-600 dark:text-slate-300 cursor-pointer">
                    <Checkbox checked={f.required} onCheckedChange={(v) => updateField(idx, { required: !!v })} data-testid={`depot-form-field-required-${idx}`} />
                    Zorunlu
                  </label>
                  {startStop.start_stop_enabled && f.ss_phase && (
                    <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded ${f.ss_phase === 'start' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`} data-testid={`depot-form-field-ss-badge-${idx}`}>
                      {useI18nStore.getState().t(f.ss_phase === 'start' ? 'startStopAdmin.tAtStart' : 'startStopAdmin.tAtStop')}
                    </span>
                  )}
                  <button onClick={() => removeField(idx)} className="text-rose-400 hover:text-rose-600 p-1" title="Sil" data-testid={`depot-form-field-remove-${idx}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
                <div className="flex items-center gap-2 ml-12">
                  <Input
                    value={f.key}
                    onChange={(e) => updateField(idx, { key: slugifyKey(e.target.value) })}
                    placeholder="anahtar (otomatik)"
                    className="h-6 text-[11px] font-mono w-40 bg-white dark:bg-slate-900"
                    data-testid={`depot-form-field-key-${idx}`}
                  />
                  <Input
                    value={f.help || ''}
                    onChange={(e) => updateField(idx, { help: e.target.value })}
                    placeholder="Açıklama (opsiyonel)"
                    className="h-6 text-[11px] flex-1 bg-white dark:bg-slate-900"
                  />
                </div>
                {(f.type === 'time' || f.type === 'date') && (
                  <div className="ml-12 mt-1.5">
                    <label
                      className="inline-flex items-center gap-1.5 text-[11px] text-slate-600 dark:text-slate-300 cursor-pointer"
                      data-testid={`depot-form-field-default-now-label-${idx}`}
                    >
                      <Checkbox
                        checked={!!f.default_now}
                        onCheckedChange={(v) => updateField(idx, { default_now: !!v })}
                        data-testid={`depot-form-field-default-now-${idx}`}
                      />
                      <span>
                        {f.type === 'time'
                          ? 'Şu anki saatle otomatik doldur'
                          : 'Bugünün tarihiyle otomatik doldur'}
                      </span>
                      <span className="text-[10px] text-slate-400">
                        (örn. bitiş saati için açın, başlangıç için kapalı bırakın)
                      </span>
                    </label>
                  </div>
                )}
                {f.type === 'select' && (
                  <div className="mt-2 ml-12">
                    <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Seçenekler</p>
                    <div className="space-y-1">
                      {(f.options || []).map((opt, oi) => (
                        <div key={oi} className="flex items-center gap-1">
                          <Input
                            value={opt}
                            onChange={(e) => {
                              const next = [...(f.options || [])];
                              next[oi] = e.target.value;
                              updateField(idx, { options: next });
                            }}
                            className="h-6 text-[11px]"
                            placeholder={`Seçenek ${oi + 1}`}
                          />
                          <button onClick={() => updateField(idx, { options: (f.options || []).filter((_, i) => i !== oi) })} className="text-rose-400 hover:text-rose-600 p-0.5">
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={() => updateField(idx, { options: [...(f.options || []), ''] })}
                        className="text-[11px] text-blue-600 hover:underline flex items-center gap-1"
                      >
                        <Plus className="h-3 w-3" /> Seçenek ekle
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}

          {!loading && (
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={insertDefaults}
                className="flex-1 border-2 border-dashed border-blue-300 dark:border-blue-700 rounded-lg py-3 text-sm font-medium text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition flex items-center justify-center gap-1"
                data-testid="depot-form-defaults-add"
                title="Başlangıç/Bitiş KM ve Saat alanlarını tek tıkla ekle"
              >
                ✨ Varsayılan Alanları Ekle
                <span className="text-[10px] text-blue-500 ml-1">(KM ×2 · Saat ×2)</span>
              </button>
              <button
                onClick={addField}
                className="flex-1 border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-lg py-3 text-sm text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 hover:border-blue-300 transition flex items-center justify-center gap-1"
                data-testid="depot-form-field-add"
              >
                <Plus className="h-4 w-4" /> Yeni alan ekle
              </button>
            </div>
          )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 p-3 border-t border-slate-200 dark:border-slate-700">
          <p className="text-[11px] text-slate-400">
            {fields.length} alan · Çalışanlar her atama günü için bu formu dolduracak.
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>İptal</Button>
            <Button size="sm" onClick={save} disabled={saving || loading} data-testid="depot-form-template-save">
              {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
              Kaydet
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
